import React from 'react';
import styled from 'styled-components';
import UploadFileIcon from '../Icons/UploadFileIcon';
import Typography from '../Typography';
import { theme } from '../../theme';
import { useTranslation } from 'react-i18next';
import Tooltip from '../Tooltip/Tooltip';
import InfoCircleOutlineIcon from '../Icons/InfoCircleOutlineIcon';
import WarningOutlineIcon from '../Icons/WarningOutlineIcon';
import { getVehicleIcon, getDefaultIconProps } from '../../utils/vehicleIcons';


const InputImage = ({ imgSrc, onChangeFile, fileInputRef, inputFileId, errors, warning, required = false, vehicleType }) => {
  const { t } = useTranslation('userProfile');

  // Obtener el icono correspondiente al tipo de vehículo
  const VehicleIconComponent = getVehicleIcon(vehicleType);
  const iconProps = {
    ...getDefaultIconProps(theme.fill.brandMain),
    width: 64,  // Ancho personalizado
    height: 64  // Alto personalizado
  };

  return (
    <Image>
      <ImageContainer>
        {imgSrc ? (
          <ImageWrapper>
            <ImagePreview
              src={imgSrc}
              alt="Vista previa"
            />
            <ImageInputBlock>
              <InputFile
                type="file"
                ref={fileInputRef}
                id={inputFileId}
                onChange={onChangeFile}
                accept="image/*"
                required={required}
              />
              <InputFileLabel
                htmlFor={inputFileId}
                className={errors.file ? 'error' : ''}
              >
                <LabelContentWrapper>
                  <LabelButtonHighlighted>
                    <UploadFileIcon fill={theme.colors.defaultStrong} />
                    <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                      {t('garage.vehicle.imageUpload')}
                    </Typography>
                  </LabelButtonHighlighted>
                  <Tooltip
                    text={t('garage.vehicle.imageRecommendations')}
                    maxWidth='200px'
                    position="left"
                  >
                    <LabelButton>
                      <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
                    </LabelButton>
                  </Tooltip>
                </LabelContentWrapper>
                {/* {errors.file && <ErrorMessage>{errors.file}</ErrorMessage>} */}
                {/* {errors.file && <ErrorMessage className='error'>{errors.file}</ErrorMessage>} */}

              </InputFileLabel>
            </ImageInputBlock>
          </ImageWrapper>
        ) : (
          <EmptyImageWrapper>
            <VehicleIconComponent {...iconProps} />
            <ImageInputBlock>
              <InputFile
                type="file"
                id={inputFileId}
                ref={fileInputRef}
                onChange={onChangeFile}
                required={required}
              />
              <InputFileLabel
                htmlFor={inputFileId}
                className={errors.file ? 'error' : ''}
              >
                <LabelContentWrapperEmpty>
                  <LabelButton>
                    <UploadFileIcon fill={theme.colors.defaultStrong} />
                    <Typography as="p" $variant="body-1-semibold" color={theme.colors.defaultStrong}>
                      {t('garage.vehicle.imageUpload')}

                    </Typography>
                  </LabelButton>
                  <Tooltip
                    text={t('garage.vehicle.imageRecommendations')}
                    maxWidth='200px'
                    position="left"
                  >
                    <LabelButton>
                      <InfoCircleOutlineIcon width="20px" height="20px" fill={theme.colors.defaultSubtle} />
                    </LabelButton>
                  </Tooltip>
                </LabelContentWrapperEmpty>
                {/* {errors.file && <ErrorMessage className='error'>{errors.file}</ErrorMessage>} */}
              </InputFileLabel>
            </ImageInputBlock>
          </EmptyImageWrapper>
        )}
      </ImageContainer>
      {errors.file && (
        <StyledError as="p" $variant="body-2-medium" color={theme.colors.errorMain}>
          {errors.file}
        </StyledError>
      )}
      {warning && (
        <StyledWarning as="p" $variant="body-2-medium" color={theme.colors.warningMain}>
          <WarningOutlineIcon width="20px" height="20px" fill={theme.colors.warningMain} />
          {warning}
        </StyledWarning>
      )}
    </Image>
  );
};

export default InputImage;

const Image = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  background-color: ${({ theme }) => theme.fill.defaultSubtle};
  width: 100%;
  aspect-ratio: 4 / 3;
  box-shadow: 0px 0px 0px 1px ${({ theme }) => theme.border.defaultWeak};
  border-radius: ${({ theme }) => theme.radius.sm};
  transition: all 0.3s;
  height: fit-content;

  &:hover {
    background-color: ${({ theme }) => theme.fill.defaultWeak};
  }
`;


const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  // height: 100%;
  aspect-ratio: 4/3;
`;

const EmptyImageWrapper = styled(ImageWrapper)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ImagePreview = styled.img`
  width: 100%;
  // height: 100%;
  aspect-ratio: 4/3;
  object-fit: cover;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const ImageInputBlock = styled.div`
  position: absolute;
  top: 0px;
  left: 0px;
  right: 0px;
  bottom: 0px;
`;

const InputFile = styled.input`
  width: 0px;
  height: 0px;
  opacity: 0;
  overflow: hidden !important;
  position: absolute;
  z-index: -1;
`;

const InputFileLabel = styled.label`
  cursor: pointer;
  color: ${({ theme }) => theme.colors.defaultStrong};
  font-variant-numeric: lining-nums tabular-nums;
  font-feature-settings: 'ss01' on, 'ss05' on, 'ss06' on, 'ss07' on;
  font-family: "MonaSans";
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 150%; /* 24px */
  // border-radius: ${({ theme }) => theme.radius.sm};
  // border: 1px solid transparent;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-direction: column;

  // &:hover {
  //   border-color: ${({ theme }) => theme.border.defaultWeak};
  // }

  .error {
    position: absolute;
    bottom: 12px;
  }
`;

// const LabelContent = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 8px;
//   padding: 8px 12px;
//   border-radius: 40px;
//   margin-bottom: 40px;
// `;

const LabelContentWrapper = styled.div`
  display: flex;
  // flex-direction: column;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.sizing.sm};
  padding: ${({ theme }) => theme.sizing.sm};
  background: linear-gradient(180deg, rgba(16, 17, 15, 0.00) 0%, rgba(16, 17, 15, 0.00) 6.67%, rgba(16, 17, 15, 0.01) 13.33%, rgba(16, 17, 15, 0.02) 20%, rgba(16, 17, 15, 0.04) 26.67%, rgba(16, 17, 15, 0.07) 33.33%, rgba(16, 17, 15, 0.10) 40%, rgba(16, 17, 15, 0.13) 46.67%, rgba(16, 17, 15, 0.17) 53.33%, rgba(16, 17, 15, 0.20) 60%, rgba(16, 17, 15, 0.23) 66.67%, rgba(16, 17, 15, 0.26) 73.33%, rgba(16, 17, 15, 0.28) 80%, rgba(16, 17, 15, 0.29) 86.67%, rgba(16, 17, 15, 0.30) 93.33%, rgba(16, 17, 15, 0.30) 100%);
  width: 100%;
  border-bottom-left-radius: ${({ theme }) => theme.radius.sm};
  border-bottom-right-radius: ${({ theme }) => theme.radius.sm};
`;

const LabelContentWrapperEmpty = styled(LabelContentWrapper)`
  background: none;
`;

const LabelButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: ${({ theme }) => theme.radius.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  background: ${({ theme }) => theme.fill.defaultMain};
  border: 1px solid ${({ theme }) => theme.border.defaultWeak};
`;

const LabelButtonHighlighted = styled(LabelButton)`
  box-shadow: rgba(16, 17, 15, 0.20) 0px 0px 24px;
`;

const StyledError = styled(Typography)`
  padding: ${({ theme }) => theme.sizing.xs};
`;

const StyledWarning = styled(Typography)`
  padding-top: ${({ theme }) => theme.sizing.xs};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ theme }) => theme.sizing.xs};
  padding: ${({ theme }) => theme.sizing.xs};
  border-radius: ${({ theme }) => theme.radius.sm};
  background-color: ${({ theme }) => theme.fill.warningAlphaMain16};
  width: fit-content;
`;